import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import ToastMessage from '../../../../components/ToastMessage';
import { HOURS, BLOCK_GAP, ENERGY_MODES_API_VALUES, MODES } from '../../../../utils/constant';
import { frameSchedulerDataForSaving } from '../utils/helpers';
import HCServices from '../../../../services/hcServices';
import { getPermissionDeniedToastMessage, convertUnit, airRoundOff, getAirUnit } from '../../../../utils/utility';
import Icon from '../../../../utils/icons';
import I18n from '../../../../localization';
import './scheduleDetails.scss';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_NAMES_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const TIMELINE_LABELS = [
  { value: 0, label: '12\nam' },
  { value: 4, label: '04' },
  { value: 8, label: '08' },
  { value: 12, label: '12\npm' },
  { value: 18, label: '06' },
  { value: 18, label: '18' },
  { value: 24, label: '12\nam' },
];

function groupSchedulesByPattern(data) {
  if (!data) return [];

  const dayKeys = [0, 1, 2, 3, 4, 5, 6];
  const groups = [];

  const getPattern = (dayIdx) => {
    const blocks = data[dayIdx];
    if (!Array.isArray(blocks)) return '';
    return blocks.map(b => `${b.presetId}|${b.start}|${b.end}`).join('::');
  };

  const used = new Set();

  for (let i = 0; i < dayKeys.length; i++) {
    if (used.has(i)) continue;
    const blocks = data[i];
    if (!Array.isArray(blocks) || blocks.length === 0) { used.add(i); continue; }
    const pattern = getPattern(i);
    const matchingDays = [i];

    for (let j = i + 1; j < dayKeys.length; j++) {
      if (used.has(j)) continue;
      if (getPattern(j) === pattern) {
        matchingDays.push(j);
        used.add(j);
      }
    }
    used.add(i);

    groups.push({
      dayIndices: matchingDays,
      dayLabels: matchingDays.map(d => DAY_NAMES[d]).join(', '),
      blocks: data[i] || [],
    });
  }

  return groups;
}

function ScheduleTimeline({ blocks, isDarkMode }) {
  const timelineRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const updateWidth = () => {
      if (timelineRef.current) {
        setContainerWidth(timelineRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const PX_PER_HOUR = containerWidth / HOURS;
  const timeToPx = (h) => h * PX_PER_HOUR;

  const timeLabels = [
    { value: 0, label: '12', sub: 'am' },
    { value: 4, label: '04', sub: '' },
    { value: 8, label: '08', sub: '' },
    { value: 12, label: '12', sub: 'pm' },
    { value: 18, label: '06', sub: '' },
    { value: 18, label: '18', sub: '' },
    { value: 24, label: '12', sub: 'am' },
  ];

  const getTransform = (t) => {
    if (t === 0) return 'translateX(0)';
    if (t === 24) return 'translateX(-100%)';
    return 'translateX(-50%)';
  };

  const LABEL_MARGIN = 16;
  const adjustedWidth = containerWidth - LABEL_MARGIN;
  const adjustedPxPerHour = adjustedWidth / HOURS;
  const adjustedTimeToPx = (h) => h * adjustedPxPerHour;

  return (
    <div className="schedule-details-timeline-wrapper">
      <div
        ref={timelineRef}
        className="schedule-details-timeline-bar"
      >
        {blocks.map((b, i) => {
          const isFirst = i === 0;
          const isLast = i === blocks.length - 1;
          const gapLeft = isFirst ? 0 : BLOCK_GAP / 2;
          const gapRight = isLast ? 0 : BLOCK_GAP / 2;

          return (
            <div
              key={b.id}
              style={{
                position: 'absolute',
                left: adjustedTimeToPx(b.start) + gapLeft,
                width: Math.max(0, adjustedTimeToPx(b.end - b.start) - (gapLeft + gapRight)),
                height: '100%',
                background: b.color,
                borderRadius: 2,
              }}
            />
          );
        })}
      </div>
      <div className="schedule-details-timeline-ticks" style={{ position: 'relative', height: 6 }}>
        {[...Array(HOURS + 1)].map((_, i) => (
          <div
            key={`tick-${i}`}
            style={{
              position: 'absolute',
              left: adjustedTimeToPx(i),
              top: 0,
              bottom: 0,
              width: 1,
              borderLeft: i % 2 === 0
                ? `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)'}`
                : `1px dotted ${isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'}`,
            }}
          />
        ))}
      </div>
      <div className="schedule-details-timeline-labels-flex" style={{ position: 'relative', height: 28 }}>
        {[0, 4, 8, 12, 16, 20, 24].map((t) => {
          let num;
          let suffix = '';
          if (t === 0 || t === 24) { num = '12'; suffix = 'am'; }
          else if (t === 12) { num = '12'; suffix = 'pm'; }
          else if (t < 12) { num = t < 10 ? '0' + t : t + ''; }
          else { num = (t - 12) < 10 ? '0' + (t - 12) : (t - 12) + ''; }
          const transform = t === 0 ? 'translateX(0)' : t === 24 ? 'translateX(-100%)' : 'translateX(-50%)';
          return (
            <div
              key={t}
              className={`schedule-details-timeline-label-item${isDarkMode ? ' dark' : ''}`}
              style={{ position: 'absolute', left: adjustedTimeToPx(t), transform }}
            >
              <div>{num}</div>
              {suffix && <div>{suffix}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}


const ScheduleDetailsView = forwardRef(function ScheduleDetailsView({ data, isDarkMode, onScheduleClick, siteId, iqAirId, onAddSchedule, onDeleteSchedule, currentEnergyMode, onNavigateBack, hasAccess = true, maskedOwnerEmail, presets = [] }, ref) {
  const groups = groupSchedulesByPattern(data);
  const [applying, setApplying] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState(new Set());
  const [toastMessage, setToastMessage] = useState('');
  const [successToast, setSuccessToast] = useState(false);
  const [applied, setApplied] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const initialDataRef = useRef(data);

  useEffect(() => {
    if (data !== initialDataRef.current) {
      setApplied(false);
    }
  }, [data]);

  const handleBackPress = () => {
    if (!applied) {
      setShowDiscardConfirm(true);
    } else if (onNavigateBack) {
      onNavigateBack();
    }
  };

  useImperativeHandle(ref, () => ({
    handleBackPress,
  }));

  const handleApply = async () => {
    if (!hasAccess) {
      setToastMessage(getPermissionDeniedToastMessage(maskedOwnerEmail));
      setTimeout(() => setToastMessage(''), 4000);
      return;
    }
    if (!siteId || !iqAirId || !data) return;
    setApplying(true);
    try {
      const payload = frameSchedulerDataForSaving(data);
      await HCServices.setCustomSchedules(payload, siteId, iqAirId);
      
      // Always switch to SCHEDULE mode after saving schedules
      try {
        const modePayload = {
          'energy-mode': ENERGY_MODES_API_VALUES.SCHEDULE
        };
        await HCServices.setAirDeviceSettings(modePayload, siteId, iqAirId);
      } catch (error) {
        console.error('Error switching energy mode:', error);
      }
      
      setApplied(true);
      setSuccessToast(true);
      setTimeout(() => {
        setSuccessToast(false);
        setApplying(false);
        // Navigate back to main page after success
        if (onNavigateBack) {
          onNavigateBack();
        }
      }, 1500);
    } catch (err) {
      console.error('Error applying schedules:', err);
      setApplying(false);
    }
  };

  const toggleDeleteMode = () => {
    setDeleteMode((prev) => !prev);
    setSelectedForDelete(new Set());
  };

  const toggleSelectForDelete = (idx) => {
    setSelectedForDelete((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
        if (next.size >= groups.length) {
          next.delete(idx);
        }
      }
      return next;
    });
  };

  const coveredDaySet = new Set();
  for (let i = 0; i < 7; i++) {
    if (Array.isArray(data?.[i]) && data[i].length > 0) coveredDaySet.add(i);
  }
  const missingDays = [0,1,2,3,4,5,6].filter(d => !coveredDaySet.has(d));
  const allDaysScheduled = missingDays.length === 0;
  const isApplyDisabled = applying || deleteMode || applied || !allDaysScheduled;

  const totalDaysCovered = groups.reduce((sum, g) => sum + g.dayIndices.length, 0);
  const allDaysCovered = totalDaysCovered >= 7;
  const isDeleteDisabled = groups.length <= 1;

  const formatTemp = (temp) => {
    if (temp === null || temp === undefined) return '-';
    return `${airRoundOff(convertUnit(temp))}`;
  };

  const getSceneTempDisplay = (preset) => {
    const tempMode = preset['temperature-mode'] || MODES.AUTO;
    if (tempMode === MODES.OFF) return '-';
    const unit = getAirUnit() === 'C' ? '°C' : '°F';
    if (tempMode === MODES.HEAT) return `${formatTemp(preset['heat-setpoint'])}${unit}`;
    if (tempMode === MODES.COOL) return `${formatTemp(preset['cool-setpoint'])}${unit}`;
    return `${formatTemp(preset['heat-setpoint'])}°-${formatTemp(preset['cool-setpoint'])}${unit}`;
  };

  const usedPresetNames = new Set();
  if (data) {
    for (let i = 0; i < 7; i++) {
      if (Array.isArray(data[i])) {
        data[i].forEach((block) => {
          if (block.name) usedPresetNames.add(block.name.toLowerCase());
        });
      }
    }
  }
  const activePresets = presets.filter((p) => usedPresetNames.has((p.name || '').toLowerCase()));

  return (
    <>
    {activePresets.length > 0 && (
      <div className={`schedule-details-legend${isDarkMode ? ' dark' : ''}`}>
        {activePresets.map((preset, idx) => (
          <span key={idx} className="schedule-details-legend-item">
            <span
              className="schedule-details-legend-swatch"
              style={{ background: preset['light-color-code'] || preset.color || '#ccc' }}
            />
            <span className={`schedule-details-legend-text${isDarkMode ? ' dark' : ''}`}>
              {preset.name}
              <div className="schedule-details-legend-temp">{getSceneTempDisplay(preset)}</div>
            </span>
          </span>
        ))}
      </div>
    )}
    <div className={`schedule-details-container${isDarkMode ? ' dark' : ''}`}>
      <div className="schedule-details-header">
        <span className={`schedule-details-header-label${isDarkMode ? ' dark' : ''}`}>
          {I18n.t('mobile.iqAirScheduler.schedules', { defaultValue: 'SCHEDULES' })}
        </span>
        <div className="schedule-details-header-actions">
          <div
            onClick={() => !allDaysCovered && onAddSchedule && onAddSchedule()}
            className={`schedule-details-icon-btn${allDaysCovered ? ' disabled' : ''}`}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 5V19M5 12H19" stroke={allDaysCovered ? '#ccc' : '#00b4de'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div
            onClick={() => !isDeleteDisabled && toggleDeleteMode()}
            className={`schedule-details-icon-btn${deleteMode ? ' active' : ''}${isDeleteDisabled ? ' disabled' : ''}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 10 13" fill="none">
              <path d="M6.16113 0C6.98949 7.97531e-05 7.66113 0.671622 7.66113 1.5V1.66504H9.38086C9.65687 1.6652 9.88086 1.88899 9.88086 2.16504C9.88077 2.44101 9.65681 2.66488 9.38086 2.66504H8.68457C8.7399 2.8215 8.77148 2.98963 8.77148 3.16504V10.6006C8.7714 11.4288 8.09971 12.1004 7.27148 12.1006H2.61133C1.78296 12.1006 1.11142 11.4289 1.11133 10.6006V3.16504C1.11133 2.98955 1.14287 2.82156 1.19824 2.66504H0.5C0.224061 2.66486 8.84091e-05 2.441 0 2.16504C0 1.889 0.224006 1.66521 0.5 1.66504H2.2207V1.5C2.2207 0.671653 2.89239 0.000131287 3.7207 0H6.16113ZM2.61133 2.66504C2.33519 2.66504 2.11133 2.8889 2.11133 3.16504V10.6006C2.11142 10.8767 2.33524 11.1006 2.61133 11.1006H7.27148C7.54742 11.1004 7.7714 10.8765 7.77148 10.6006V3.16504C7.77148 2.88901 7.54748 2.66522 7.27148 2.66504H2.61133ZM3.83105 3.88574C4.10712 3.88583 4.33105 4.10965 4.33105 4.38574V9.38086C4.33092 9.65683 4.10704 9.88077 3.83105 9.88086C3.55499 9.88086 3.33119 9.65689 3.33105 9.38086V4.38574C3.33105 4.1096 3.55491 3.88574 3.83105 3.88574ZM6.05078 3.88574C6.32692 3.88574 6.55078 4.1096 6.55078 4.38574V9.38086C6.55065 9.65689 6.32684 9.88086 6.05078 9.88086C5.77472 9.88086 5.55091 9.65689 5.55078 9.38086V4.38574C5.55078 4.1096 5.77464 3.88574 6.05078 3.88574ZM3.7207 1C3.44467 1.00013 3.2207 1.22394 3.2207 1.5V1.66504H6.66113V1.5C6.66113 1.22391 6.43721 1.00008 6.16113 1H3.7207Z" fill={isDeleteDisabled ? '#ccc' : (deleteMode ? '#fff' : '#00b4de')} />
            </svg>
          </div>
        </div>
      </div>
      <div className="schedule-details-scroll-area" style={applying ? { pointerEvents: 'none', opacity: 0.6 } : undefined} onClick={(e) => e.stopPropagation()}>
        {groups.map((group, idx) => (
          <div key={idx} className="schedule-details-group-row">
            {deleteMode && (
              <div
                onClick={() => toggleSelectForDelete(idx)}
                className={`schedule-details-checkbox${selectedForDelete.has(idx) ? ' selected' : ''}`}
              >
                {selectedForDelete.has(idx) && (
                  <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                    <path d="M1 5L4.5 8.5L11 1.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
            )}
            <div
              onClick={() => !deleteMode && !applying && onScheduleClick && onScheduleClick(group.dayIndices)}
              className={`schedule-details-card${isDarkMode ? ' dark' : ''}${deleteMode ? ' delete-mode' : ''}`}
            >
              <div className="schedule-details-card-header">
                <span className={`schedule-details-schedule-name${isDarkMode ? ' dark' : ''}`}>
                  {I18n.t('mobile.iqAirScheduler.scheduleNumber', { defaultValue: 'Schedule %{number}', number: idx + 1 })}
                </span>
                <div className="schedule-details-day-labels-wrapper">
                  <span className="schedule-details-day-labels">
                    {group.dayLabels}
                  </span>
                  <span className="schedule-details-day-arrow">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6.75 4.5L11.25 9L6.75 13.5" stroke="#00b4de" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </div>
              </div>
              <ScheduleTimeline blocks={group.blocks} isDarkMode={isDarkMode} />
            </div>
          </div>
        ))}
      </div>
      <div className="schedule-details-footer">
        {deleteMode ? (
          <button
            onClick={() => {
              if (selectedForDelete.size === 0) return;
              setShowDeleteConfirm(true);
            }}
            className={`schedule-details-action-btn${selectedForDelete.size > 0 ? ' active' : ' disabled'}${isDarkMode ? ' dark' : ''}`}
          >
            {I18n.t('mobile.iqAirScheduler.delete', { defaultValue: 'Delete' })}
          </button>
        ) : (
          <button
            onClick={() => {
              if (applying || deleteMode || applied) return;
              if (!allDaysScheduled) {
                const missingNames = missingDays.map(d => DAY_NAMES[d]).join(', ');
                setToastMessage(I18n.t('mobile.iqAirScheduler.scheduleMissing', { defaultValue: 'Schedule missing for %{days}. Please add schedule(s) before applying', days: missingNames }));
                setTimeout(() => setToastMessage(''), 3000);
                return;
              }
              handleApply();
            }}
            className={`schedule-details-action-btn${isApplyDisabled ? ' disabled' : ' active'}${isDarkMode ? ' dark' : ''}`}
          >
            {applying ? I18n.t('mobile.iqAirScheduler.applying', { defaultValue: 'Applying...' }) : I18n.t('mobile.iqAirScheduler.apply', { defaultValue: 'Apply' })}
          </button>
        )}
      </div>
      <ToastMessage
        shouldDisplay={successToast}
        content={I18n.t('mobile.iqAirScheduler.changesSubmitted', { defaultValue: 'Changes submitted. May take a moment to reflect on your device.' })}
        handleClose={() => setSuccessToast(false)}
        type="success"
        fullWidth
      />
      {showDeleteConfirm && (
        <div className="schedule-details-modal-overlay">
          <div className={`schedule-details-modal-box${isDarkMode ? ' dark' : ''}`}>
            <div className="schedule-details-modal-title">{I18n.t('mobile.iqAirScheduler.deleteSchedule', { defaultValue: 'Delete Schedule' })}</div>
            <div className={`schedule-details-modal-body${isDarkMode ? ' dark' : ''}`}>
              {I18n.t('mobile.iqAirScheduler.deleteConfirmPrefix', { defaultValue: 'This will delete' })} <b>{Array.from(selectedForDelete).map(idx => I18n.t('mobile.iqAirScheduler.scheduleNumber', { defaultValue: 'Schedule %{number}', number: idx + 1 })).join(', ')}</b>
              {' '}({Array.from(selectedForDelete).flatMap(idx => groups[idx]?.dayIndices || []).map(d => DAY_NAMES_FULL[d]).join(', ')}).
              {' '}{I18n.t('mobile.iqAirScheduler.deleteConfirmSuffix', { defaultValue: 'Are you sure you want to continue?' })}
            </div>
            <div className="schedule-details-modal-actions">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="schedule-details-modal-cancel-btn"
              >
                {I18n.t('mobile.iqAirScheduler.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button
                onClick={() => {
                  const dayIndicesToClear = Array.from(selectedForDelete).flatMap(idx => groups[idx]?.dayIndices || []);
                  if (onDeleteSchedule) {
                    onDeleteSchedule(dayIndicesToClear);
                  }
                  setShowDeleteConfirm(false);
                  setDeleteMode(false);
                  setSelectedForDelete(new Set());
                }}
                className="schedule-details-modal-confirm-btn"
              >
                {I18n.t('mobile.iqAirScheduler.confirm', { defaultValue: 'Confirm' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
      {showDiscardConfirm && (
        <div className="schedule-details-modal-overlay">
          <div className={`schedule-details-modal-box${isDarkMode ? ' dark' : ''}`}>
            <div className="schedule-details-modal-title">{I18n.t('mobile.iqAirSetting.discardChanges')}</div>
            <div className={`schedule-details-modal-body${isDarkMode ? ' dark' : ''}`}>
              {I18n.t('mobile.iqAirSetting.discardConfirmMessage')}
            </div>
            <div className="schedule-details-modal-actions">
              <button
                onClick={() => setShowDiscardConfirm(false)}
                className="schedule-details-modal-cancel-btn"
              >
                {I18n.t('mobile.iqAirScheduler.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button
                onClick={() => {
                  setShowDiscardConfirm(false);
                  if (onNavigateBack) onNavigateBack();
                }}
                className="schedule-details-modal-confirm-btn"
              >
                {I18n.t('mobile.iqAirSetting.yes')}
              </button>
            </div>
          </div>
        </div>
      )}
      <ToastMessage
        shouldDisplay={!!toastMessage}
        content={toastMessage}
        handleClose={() => setToastMessage('')}
        type="error"
        isPermissionDeniedMsg={!hasAccess}
        fullWidth
      />
    </>
  );
});

export default ScheduleDetailsView;
